/**
 * Base implementation class that all platform adapters will extend
 * Provides default implementations with informative error messages
 */
import {
  IProductQueryReqest,
  ICollectionQueryRequest,
  IPageRequest,
  IBlogArticleRequest,
  ITrendingArticleRequest,
  IArticleByHandleRequest,
  IGetArticleRequest,
  IOrderQueryRequest,
  IOrderRequest,
  ICustomerQueryRequest,
  ICustomerRequest,
  ICartUpdateRequest,
  ICartItemRequest,
  ICheckoutUpdateRequest,
  IPaymentRequest,
  ISingleProductRequest,
  ISingleCollectionRequest,
  IOptionsRequest,
  IResponse,
} from "../models";
import { CommerceClient } from "./interface";

/**
 * Error thrown when a method is not yet implemented in an adapter
 */
export class NotImplementedError extends Error {
  constructor(
    public readonly adapterName: string,
    public readonly methodName: string
  ) {
    super(
      `${methodName} is not yet implemented in ${adapterName} adapter. ` +
        `This method will be available in a future version.`
    );
    this.name = "NotImplementedError";
  }
}

/**
 * Abstract base class that implements all methods with informative error messages
 * Platform adapters should extend this class and override the methods they support
 */
export abstract class BaseCommerceClient implements CommerceClient {
  /**
   * The name of the adapter (e.g., "Shopify", "Custom Backend")
   * Used in error messages for better debugging
   */
  protected abstract readonly adapterName: string;

  /**
   * Helper method to throw informative "not implemented" errors
   * @param methodName - The method name that's not implemented
   */
  protected throwNotImplemented(methodName: string): never {
    throw new NotImplementedError(this.adapterName, methodName);
  }

  // ========================================
  // PRODUCT OPERATIONS
  // ========================================

  /**
   * Retrieve a single product by ID or handle
   * @param params - Product identifier (id or handle)
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to product data
   * @throws NotImplementedError if not implemented in adapter
   */
  getProduct(
    params: ISingleProductRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getProduct");
  }

  /**
   * Retrieve multiple products with filtering and pagination
   * @param params - Query parameters for filtering, sorting, and pagination
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to paginated products response
   * @throws NotImplementedError if not implemented in adapter
   */
  getProducts(
    params: IProductQueryReqest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getProducts");
  }

  /**
   * Retrieve multiple products by their handles
   * @param handles - Array of product handles
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to array of products
   * @throws NotImplementedError if not implemented in adapter
   */
  getProductsByHandles(
    handles: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getProductsByHandles");
  }

  // ========================================
  // COLLECTION OPERATIONS
  // ========================================

  /**
   * Retrieve a single collection by ID or handle
   * @param params - Collection identifier (id or handle)
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to collection data
   * @throws NotImplementedError if not implemented in adapter
   */
  getCollection(
    params: ISingleCollectionRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getCollection");
  }

  /**
   * Retrieve multiple collections with filtering and pagination
   * @param params - Query parameters for filtering, sorting, and pagination
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to paginated collections response
   * @throws NotImplementedError if not implemented in adapter
   */
  getCollections(
    params: ICollectionQueryRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getCollections");
  }

  // ========================================
  // ORDER OPERATIONS
  // ========================================

  /**
   * Retrieve a single order by ID
   * @param id - Order identifier
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to order data
   * @throws NotImplementedError if not implemented in adapter
   */
  getOrder(id: string, options?: IOptionsRequest): Promise<IResponse> {
    this.throwNotImplemented("getOrder");
  }

  /**
   * Retrieve multiple orders with filtering and pagination
   * @param params - Query parameters for filtering, sorting, and pagination
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to paginated orders response
   * @throws NotImplementedError if not implemented in adapter
   */
  getOrders(
    params: IOrderQueryRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getOrders");
  }

  /**
   * Create a new order
   * @param data - Order data to create
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to created order
   * @throws NotImplementedError if not implemented in adapter
   */
  createOrder(
    data: IOrderRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("createOrder");
  }

  /**
   * Update an existing order
   * @param id - Order identifier
   * @param data - Partial order data to update
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to updated order
   * @throws NotImplementedError if not implemented in adapter
   */
  updateOrder(
    id: string,
    data: Partial<IOrderRequest>,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("updateOrder");
  }

  /**
   * Retrieve order history for a customer
   * @param customerAccessToken - Customer access token
   * @param page - Page number for pagination (default: 1)
   * @param limit - Number of orders per page (default: 10)
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to order history
   * @throws NotImplementedError if not implemented in adapter
   */
  getOrderHistory(
    customerAccessToken: string,
    page = 1,
    limit = 10,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getOrderHistory");
  }

  /**
   * Retrieve a specific order for a customer
   * @param customerAccessToken - Customer access token
   * @param orderId - Order identifier
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to order data
   * @throws NotImplementedError if not implemented in adapter
   */
  getOrderById(
    customerAccessToken: string,
    orderId: string,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getOrderById");
  }

  // ========================================
  // CUSTOMER OPERATIONS
  // ========================================

  /**
   * Retrieve a single customer by ID
   * @param id - Customer identifier
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to customer data
   * @throws NotImplementedError if not implemented in adapter
   */
  getCustomer(id: string, options?: IOptionsRequest): Promise<IResponse> {
    this.throwNotImplemented("getCustomer");
  }

  /**
   * Retrieve multiple customers with filtering and pagination
   * @param params - Query parameters for filtering, sorting, and pagination
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to paginated customers response
   * @throws NotImplementedError if not implemented in adapter
   */
  getCustomers(
    params: ICustomerQueryRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getCustomers");
  }

  /**
   * Create a new customer account
   * @param data - Customer data to create
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to created customer
   * @throws NotImplementedError if not implemented in adapter
   */
  createCustomer(
    data: ICustomerRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("createCustomer");
  }

  /**
   * Update an existing customer account
   * @param id - Customer identifier
   * @param data - Partial customer data to update
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to updated customer
   * @throws NotImplementedError if not implemented in adapter
   */
  updateCustomer(
    id: string,
    data: Partial<ICustomerRequest>,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("updateCustomer");
  }

  // ========================================
  // CART OPERATIONS
  // ========================================

  /**
   * Retrieve a shopping cart by ID
   * @param id - Cart identifier
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to standardized response with cart data
   * @throws NotImplementedError if not implemented in adapter
   */
  getCart(id: string, options?: IOptionsRequest): Promise<IResponse> {
    this.throwNotImplemented("getCart");
  }

  /**
   * Create a new shopping cart
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to standardized response with created cart
   * @throws NotImplementedError if not implemented in adapter
   */
  createCart(options?: IOptionsRequest): Promise<IResponse> {
    this.throwNotImplemented("createCart");
  }

  /**
   * Update an existing shopping cart
   * @param id - Cart identifier
   * @param data - Cart update data
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to standardized response with updated cart
   * @throws NotImplementedError if not implemented in adapter
   */
  updateCart(
    id: string,
    data: ICartUpdateRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("updateCart");
  }

  /**
   * Add items to a shopping cart
   * @param cartId - Cart identifier
   * @param items - Array of items to add
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to standardized response with updated cart
   * @throws NotImplementedError if not implemented in adapter
   */
  addToCart(
    cartId: string,
    items: ICartItemRequest[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("addToCart");
  }

  /**
   * Remove items from a shopping cart
   * @param cartId - Cart identifier
   * @param itemIds - Array of item IDs to remove
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to standardized response with updated cart
   * @throws NotImplementedError if not implemented in adapter
   */
  removeFromCart(
    cartId: string,
    itemIds: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("removeFromCart");
  }

  // ========================================
  // CHECKOUT OPERATIONS
  // ========================================

  /**
   * Create a new checkout session
   * @param payload - Checkout data
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to checkout session
   * @throws NotImplementedError if not implemented in adapter
   */
  createCheckout(payload: any, options?: IOptionsRequest): Promise<IResponse> {
    this.throwNotImplemented("createCheckout");
  }

  /**
   * Update an existing checkout session
   * @param id - Checkout identifier
   * @param data - Checkout update data
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to updated checkout
   * @throws NotImplementedError if not implemented in adapter
   */
  updateCheckout(
    id: string,
    data: ICheckoutUpdateRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("updateCheckout");
  }

  /**
   * Complete a checkout and create an order
   * @param id - Checkout identifier
   * @param paymentData - Payment information
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to created order
   * @throws NotImplementedError if not implemented in adapter
   */
  completeCheckout(
    id: string,
    paymentData: IPaymentRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("completeCheckout");
  }

  // ========================================
  // PAGE OPERATIONS
  // ========================================

  /**
   * Retrieve a page by its handle
   * @param params - Page parameters including handle
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to page data
   * @throws NotImplementedError if not implemented in adapter
   */
  getPageByHandle(
    params: IPageRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getPageByHandle");
  }

  // ========================================
  // ARTICLE/BLOG OPERATIONS
  // ========================================

  /**
   * Retrieve a single article
   * @param params - Query parameters for filtering articles
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to article data
   * @throws NotImplementedError if not implemented in adapter
   */
  getArticle(
    params?: IGetArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getArticle");
  }

  /**
   * Retrieve blog articles with optional filtering
   * @param params - Query parameters for category and limit
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to articles array
   * @throws NotImplementedError if not implemented in adapter
   */
  getBlogArticles(
    params: IBlogArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getBlogArticles");
  }

  /**
   * Retrieve trending articles with optional filtering
   * @param params - Query parameters for category
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to trending articles
   * @throws NotImplementedError if not implemented in adapter
   */
  getTrendingArticles(
    params: ITrendingArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getTrendingArticles");
  }

  /**
   * Retrieve a specific article by handle
   * @param params - Article parameters including category and handle
   * @param options - Request options (headers, timeout, etc.)
   * @returns Promise resolving to article data
   * @throws NotImplementedError if not implemented in adapter
   */
  getArticleByHandle(
    params: IArticleByHandleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    this.throwNotImplemented("getArticleByHandle");
  }
}
